import React from "react";
import {
  Sun,
  PlayCircle,
  CheckCircle2,
  ArrowRight,
  Quote,
  Mail,
  Sparkles,
  MapPin,
  ShieldCheck,
  Clock,
  FileCheck2,
  HeartHandshake,
} from "lucide-react";
import { toast } from "sonner";
import { PHOTOS, SPAIN_HERO, SPAIN_VILLAGE } from "@/lib/site-config";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import BookSection from "@/components/site/BookSection";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import usePageMeta from "@/lib/use-page-meta";

const YOUTUBE_ID = "pEZW9zRrE3g";

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section id="top" className="relative pt-28 pb-20 lg:pt-40 lg:pb-28 overflow-hidden editorial">
    <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
    <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <div className="fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium" data-testid="hero-badge">
          <Sparkles className="w-3.5 h-3.5" />
          NLV Approved · Personalised Support
        </div>

        <h1
          className="fade-up delay-100 font-serif text-[2.6rem] sm:text-[3.4rem] lg:text-[4.6rem] text-[var(--navy)] mt-6"
          data-testid="hero-headline"
        >
          Retire to <em>Spain.</em>
          <br />
          <span className="underline-swoosh">Properly.</span>
        </h1>

        <p
          className="fade-up delay-200 mt-7 text-lg lg:text-xl text-[var(--ink-soft)] max-w-xl leading-relaxed"
          data-testid="hero-subhead"
        >
          A personal, step-by-step Non-Lucrative Visa programme for people who want clarity,
          structure and proper support — so you can prepare your move without second-guessing
          every form, document and deadline.
        </p>

        <div className="fade-up delay-300 mt-10 flex flex-wrap gap-4" data-testid="hero-ctas">
          <a
            href="#book"
            className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
            data-testid="hero-cta-primary"
          >
            Find my next step <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href="#video"
            className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--navy)]/25 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] font-medium"
            data-testid="hero-cta-secondary"
          >
            <PlayCircle className="w-5 h-5" /> Watch the explainer
          </a>
        </div>

        <div className="fade-up delay-400 mt-12 flex items-center gap-6 text-sm text-[var(--muted)]">
          <div className="flex -space-x-3">
            {[PHOTOS.carley_solo, PHOTOS.carley_daughter, PHOTOS.carley_son].map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className="w-10 h-10 rounded-full object-cover border-2 border-[var(--cream)]"
              />
            ))}
          </div>
          <div>
            <span className="block text-[var(--ink)] font-semibold">Hundreds of people guided</span>
            <span className="block text-xs">Personally mentored by Carley Chaplin</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 relative fade-in delay-300">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
          <img
            src={SPAIN_HERO}
            alt="A sun-drenched Spanish village"
            className="absolute inset-0 w-full h-full object-cover slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy)]/55 via-transparent to-transparent" />
          <div className="absolute top-6 left-6 right-6 text-[var(--cream)]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-2 opacity-90">
              <MapPin className="w-3.5 h-3.5" /> Costa del Sol · 24°C
            </div>
            <p className="font-serif text-2xl leading-tight max-w-[70%]">
              "It's not a dream. It's a plan."
            </p>
          </div>
        </div>

        <div className="hidden lg:block absolute -bottom-12 -left-16 w-44 h-56 rounded-2xl overflow-hidden shadow-2xl rotate-[-6deg] border-[6px] border-[var(--cream)]">
          <img src={PHOTOS.carley_solo} alt="Carley in Spain" className="w-full h-full object-cover" />
        </div>
        <div className="hidden lg:flex absolute -top-6 -right-6 px-4 py-3 rounded-2xl bg-[var(--navy)] text-[var(--cream)] items-center gap-2 rotate-3 shadow-xl">
          <ShieldCheck className="w-5 h-5 text-[var(--gold)]" />
          <div className="text-xs leading-tight">
            <div className="font-semibold">Visa-approved</div>
            <div className="opacity-80">framework</div>
          </div>
        </div>
      </div>
    </div>

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 mt-20 fade-in delay-500">
      <div className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
        <span className="h-px flex-1 bg-[var(--ink)]/15" />
        Trusted approach
        <span className="h-px flex-1 bg-[var(--ink)]/15" />
      </div>
      <div className="overflow-hidden mt-6">
        <div className="flex gap-12 marquee whitespace-nowrap font-serif text-2xl text-[var(--navy)]/70">
          {Array.from({ length: 2 }).map((_, k) => (
            <React.Fragment key={k}>
              <span>· Non-Lucrative Visa</span>
              <span>· Document Review</span>
              <span>· Apostilles &amp; Translations</span>
              <span>· Financial Proof</span>
              <span>· Padrón &amp; Residency</span>
              <span>· Healthcare Setup</span>
              <span>· Direct WhatsApp Support</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- STOP / PAIN ---------------- */
const StopSection = () => (
  <section className="relative py-24 lg:py-32 bg-[var(--navy)] text-[var(--cream)] overflow-hidden grain editorial">
    <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
      <img src={SPAIN_VILLAGE} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
      <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium" data-testid="stop-eyebrow">
        🚫 Retirees: stop
      </div>
      <h2 className="font-serif text-4xl lg:text-6xl mt-6 leading-[1.05]" data-testid="stop-headline">
        Don't apply for your NLV until you've{" "}
        <em className="text-[var(--gold)]">read this.</em>
      </h2>
      <div className="mt-12 grid md:grid-cols-2 gap-x-14 gap-y-8 text-[1.05rem] leading-relaxed text-[var(--cream)]/85">
        <p>
          Would you like to finally retire to Spain in the next 6–12 months — without
          second-guessing every form, worrying about getting rejected, or trying to
          piece everything together from Google whilst guessing at the Spanish red tape?
        </p>
        <p>
          And to feel confident you're doing it properly — that your documents are correct,
          your timeline actually works, and you're not missing something that could delay
          everything?
        </p>
      </div>

      <div className="mt-14 border-t border-[var(--cream)]/15 pt-10">
        <p className="font-serif italic text-2xl lg:text-3xl text-[var(--cream)] max-w-3xl">
          "I'm looking for just a small number of people who are serious about getting their
          Non-Lucrative Visa approved — who want clarity, structure, and proper support."
        </p>
        <p className="mt-4 text-sm uppercase tracking-[0.25em] text-[var(--gold)]">
          — Carley Chaplin
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- VIDEO ---------------- */
const VideoSection = () => (
  <section id="video" className="py-24 lg:py-32 bg-[var(--cream-deep)] relative">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium" data-testid="video-eyebrow">
          The explainer
        </div>
        <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]" data-testid="video-headline">
          A few minutes that could change <em className="text-[var(--terracotta)]">your retirement.</em>
        </h2>
        <p className="mt-5 text-[var(--ink-soft)]">
          Watch Carley explain exactly what NLV Approved is, who it's for, and how the next 6–12 months can look.
        </p>
      </div>

      <div className="mt-14 relative rounded-[2rem] overflow-hidden shadow-2xl bg-[var(--navy)]" data-testid="video-embed-wrapper">
        <div className="relative pt-[56.25%]">
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${YOUTUBE_ID}?rel=0&modestbranding=1`}
            title="NLV Approved — Programme Explainer"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid="video-iframe"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- PROGRAMME ---------------- */
const Programme = () => {
  const items = [
    {
      icon: HeartHandshake,
      title: "Direct access to Carley",
      body: "For questions, support and guidance throughout your application — not a chatbot, not a junior, not a forum.",
    },
    {
      icon: Clock,
      title: "A clear 6–12 month plan",
      body: "Step-by-step and tailored to your timeline so you can actually retire to Spain within the next 6–12 months.",
    },
    {
      icon: FileCheck2,
      title: "Spanish forms, simplified",
      body: "Walk through every form together so you stop second-guessing and start submitting with confidence.",
    },
    {
      icon: ShieldCheck,
      title: "Financial proof, sorted",
      body: "Guidance on the financial requirements, translations, apostilles and supporting documents.",
    },
    {
      icon: CheckCircle2,
      title: "Pre-submission review",
      body: "We check everything before you submit, so you know it's correct the first time.",
    },
    {
      icon: Sparkles,
      title: "No solicitor price tag",
      body: "Real, personal support — without the cost or complexity of going down the legal-firm route.",
    },
  ];

  return (
    <section id="programme" className="py-24 lg:py-32 relative editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
              By working together
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl text-[var(--navy)] mt-5 leading-[1.02]" data-testid="programme-headline">
              Everything you need.<br />
              <em>Nothing you don't.</em>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[var(--ink-soft)] text-lg">
            This is about doing it properly — and avoiding the mistakes that cost people
            time, money and approvals. Real support. Real answers. A clear plan.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {items.map((it, i) => (
            <div
              key={i}
              className="soft-card rounded-2xl p-7 hover:bg-white transition-colors duration-500 group"
              data-testid={`programme-item-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[var(--terracotta)]/10 text-[var(--terracotta)] flex items-center justify-center mb-5 group-hover:bg-[var(--terracotta)] group-hover:text-[var(--cream)] transition-colors duration-500">
                <it.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-2xl text-[var(--navy)] leading-tight">{it.title}</h3>
              <p className="mt-3 text-[var(--ink-soft)] text-[0.95rem] leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- SOLICITOR BAND ---------------- */
const SolicitorBand = () => (
  <section className="py-20 lg:py-24 bg-[var(--cream-deep)] editorial" data-testid="nlv-solicitor-band">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <h2 className="font-serif text-3xl lg:text-5xl text-[var(--navy)] leading-[1.08]">
        Personal support. A positive experience.{" "}
        <em className="text-[var(--terracotta)]">No solicitor price tag.</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg leading-relaxed max-w-2xl mx-auto">
        Clear, practical guidance and personal oversight throughout your move, with introductions
        to regulated specialists when legal or tax advice is appropriate.
      </p>
    </div>
  </section>
);

/* ---------------- SEQUENCING ---------------- */
const Sequencing = () => (
  <section className="relative py-24 lg:py-28 bg-[var(--navy)] text-[var(--cream)] overflow-hidden grain editorial" data-testid="nlv-sequencing">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 relative">
      <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium">
        A visa application is a sequence
      </div>
      <h2 className="font-serif text-4xl lg:text-5xl mt-6 leading-[1.08]" data-testid="nlv-sequencing-headline">
        One missing step can hold up{" "}
        <em className="text-[var(--gold)]">everything that follows.</em>
      </h2>
      <p className="mt-8 text-[1.05rem] leading-relaxed text-[var(--cream)]/85 max-w-3xl">
        The documents you order, their issue dates, translations, apostilles, financial evidence,
        healthcare and appointment timing need to work together. NLV Approved helps you build the
        process in the right order and prepare the complete application with fewer avoidable
        surprises.
      </p>
    </div>
  </section>
);

/* ---------------- WHO FOR ---------------- */
const WhoFor = () => {
  const yes = [
    "Serious about retiring to Spain in the next 6–12 months",
    "Ready to follow a clear, structured plan",
    "Open to guidance and doing things properly",
    "Willing to invest in getting it right the first time",
    "Done with confusion, conflicting advice, and delays",
    "Ready to begin in the next few weeks",
  ];
  const noMatter = [
    "Just starting and feeling overwhelmed",
    "Unsure if you even qualify",
    "Stuck on the Spanish forms or financial proof",
    "Worried about getting something wrong and being rejected",
  ];

  return (
    <section className="py-24 lg:py-32 bg-[var(--cream-deep)]/60 relative editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
            Is this you?
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]" data-testid="whofor-headline">
            It doesn't matter where you are <em>right now.</em>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mt-14">
          <div className="rounded-3xl p-8 lg:p-10 bg-white border border-black/5" data-testid="whofor-doesnt-matter">
            <h3 className="font-serif text-2xl text-[var(--navy)]">It doesn't matter if you're…</h3>
            <ul className="mt-6 space-y-4">
              {noMatter.map((t, i) => (
                <li key={i} className="flex gap-3 text-[var(--ink-soft)]">
                  <span className="w-6 h-6 rounded-full bg-[var(--cream-deep)] text-[var(--terracotta)] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sun className="w-3.5 h-3.5" />
                  </span>
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl p-8 lg:p-10 bg-[var(--navy)] text-[var(--cream)]" data-testid="whofor-must-be">
            <h3 className="font-serif text-2xl">All that matters is you're someone who is…</h3>
            <ul className="mt-6 space-y-4">
              {yes.map((t, i) => (
                <li key={i} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[var(--gold)] flex-shrink-0 mt-1" />
                  <span className="text-[var(--cream)]/90 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <a
              href="#book"
              className="pill mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
              data-testid="whofor-cta"
            >
              That's me — let's talk <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- ABOUT CARLEY ---------------- */
const About = () => (
  <section id="about" className="py-24 lg:py-32 relative overflow-hidden editorial">
    <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5 relative">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-xl">
          <img src={PHOTOS.family_yacht} alt="Carley with her family" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-10 -right-6 lg:-right-12 w-44 h-56 rounded-2xl overflow-hidden shadow-xl border-4 border-[var(--cream)] rotate-[5deg]">
          <img src={PHOTOS.family_kids_selfie} alt="Family in Spain" className="w-full h-full object-cover" />
        </div>
        <span className="tape -top-3 left-12" />
      </div>

      <div className="lg:col-span-7">
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
          Meet your guide
        </div>
        <h2 className="font-serif text-4xl lg:text-6xl text-[var(--navy)] mt-5 leading-[1.02]" data-testid="about-headline">
          Hi, I'm <em>Carley.</em>
        </h2>
        <div className="mt-6 space-y-5 text-[var(--ink-soft)] text-[1.05rem] leading-relaxed">
          <p>
            I built <strong className="text-[var(--navy)]">NLV Approved</strong> because I kept seeing the same thing —
            people worried about getting it wrong, overwhelmed by conflicting advice, and trying to
            do everything themselves whilst the months slipped by.
          </p>
          <p>
            So I built a way to support you through it — without the cost or complexity of a solicitor.
            I've now <strong className="text-[var(--navy)]">personally worked with hundreds of people</strong> to develop
            their successful Spain retirement plan.
          </p>
          <p>
            Because of the level of support and personal access, spaces are limited. I only take on a
            small number of people at a time to make sure you actually get the help you need.
          </p>
          <p className="font-serif italic text-xl text-[var(--navy)]">
            "If you've been thinking about retiring to Spain but keep putting it off because you're unsure — this is your sign."
          </p>
        </div>

        <div className="mt-10">
          <a
            href="https://m.me/gettospain"
            target="_blank"
            rel="noreferrer"
            onClick={() => toast.success("Opening Messenger…")}
            className="pill inline-flex items-center gap-2 px-6 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
            data-testid="about-messenger-cta"
          >
            <Mail className="w-5 h-5" />
            Ready to go ahead, or just have a question? Message me on Facebook
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-3 text-xs text-[var(--muted)]">
            Sends a Messenger chat directly to Carley at m.me/gettospain
          </p>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
          <Stat n="100s" l="Retirees guided" />
          <Stat n="6–12mo" l="Avg. timeline" />
          <Stat n="1:1" l="Personal access" />
        </div>
      </div>
    </div>
  </section>
);

const Stat = ({ n, l }) => (
  <div className="border-l-2 border-[var(--terracotta)] pl-4">
    <div className="font-serif text-3xl text-[var(--navy)]">{n}</div>
    <div className="text-xs uppercase tracking-wider text-[var(--muted)] mt-1">{l}</div>
  </div>
);

/* ---------------- TESTIMONIALS ---------------- */
const Testimonials = () => {
  return (
    <section id="stories" className="py-24 lg:py-32 bg-[var(--navy)] text-[var(--cream)] relative overflow-hidden editorial grain">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--gold)] font-medium">
            Real stories
          </div>
          <h2 className="font-serif text-4xl lg:text-6xl mt-5 leading-[1.02]" data-testid="testimonials-headline">
            From <em className="text-[var(--gold)]">"someday"</em><br />to keys in hand.
          </h2>
        </div>

        {/* FEATURED testimonial — Glen with photo */}
        <div
          className="mt-16 grid lg:grid-cols-5 gap-6 rounded-[2rem] overflow-hidden bg-[var(--navy-deep)]/60 border border-[var(--gold)]/30"
          data-testid="testimonial-featured"
        >
          <div className="lg:col-span-2 relative min-h-[340px] lg:min-h-0">
            <img
              src={PHOTOS.carley_clients}
              alt="Carley with clients Glen and partner"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[var(--navy)]/60 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--cream)]">
              <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
              Met with Carley in September
            </div>
          </div>
          <figure className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
            <Quote className="w-9 h-9 text-[var(--gold)]" />
            <blockquote className="mt-5 font-serif text-xl lg:text-2xl leading-[1.4] text-[var(--cream)]">
              "THANK YOU for helping us!!! If you're considering meeting with anyone to discuss
              moving to Spain, I highly recommend Carley. She is awesome! We met with her in
              September and she <em className="text-[var(--gold)] not-italic font-medium">knows her stuff</em> — well organised and informed! We are well
              on our way to life in Spain thanks to her. She is an excellent relocation specialist!"
            </blockquote>
            <figcaption className="mt-8 pt-6 border-t border-[var(--cream)]/15">
              <div className="font-medium text-lg">Glen</div>
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--gold)] mt-1">
                @happydayglen · Moving to Spain
              </div>
            </figcaption>
          </figure>
        </div>

        {/* Craig Swanson — IF I CAN YOU CAN */}
        <figure
          className="mt-6 rounded-[2rem] p-8 lg:p-12 bg-[var(--navy-deep)]/60 border border-[var(--cream)]/10 hover:border-[var(--gold)]/40 transition-colors relative overflow-hidden"
          data-testid="testimonial-craig"
        >
          <div className="absolute -top-10 -right-10 w-56 h-56 bg-[var(--gold)]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-4">
              <div className="relative w-44 h-56 rounded-2xl overflow-hidden shadow-xl border-4 border-[var(--cream)]/20 mb-7">
                <img
                  src={PHOTOS.craig_visa}
                  alt="Craig Swanson holding his approved Spanish visa"
                  className="w-full h-full object-cover object-top"
                  data-testid="testimonial-craig-photo"
                />
              </div>
              <div className="font-serif text-4xl lg:text-5xl text-[var(--gold)] leading-none tracking-tight">
                If I can,<br />you can.
              </div>
              <div className="mt-5 text-xs uppercase tracking-[0.25em] text-[var(--cream)]/70">
                Craig Swanson · Edinburgh → Spain
              </div>
            </div>
            <blockquote className="lg:col-span-8 font-serif text-lg lg:text-xl leading-[1.55] text-[var(--cream)]/95 space-y-4">
              <p>
                "Tonight I assembled my pack for my Spanish Visa application. After multiple text
                messages, video calls, emails and a few frustrating moments (and a quick holiday in
                Spain), the moment has arrived — Wednesday, Edinburgh, 11am."
              </p>
              <p>
                "I cannot thank Carley enough. I opted for her hand-hold service, and that is
                exactly what she did — the attention to detail, support and going the extra mile
                was amazing. February, the dream, Wednesday, it's here."
              </p>
              <p className="text-[var(--gold)] italic">
                "If you are thinking about it — should I, could I, what if, or just don't know
                where to start — reach out to her and have the chat. Thank you, Carley Chaplin."
              </p>
            </blockquote>
          </div>
        </figure>

        {/* Neil & Clare — Facebook-post-styled card */}
        <figure
          className="mt-6 rounded-[2rem] overflow-hidden bg-white text-[var(--ink)] shadow-xl border border-black/5"
          data-testid="testimonial-neil-clare"
        >
          <div className="grid md:grid-cols-5">
            <div className="md:col-span-2 relative min-h-[320px] md:min-h-[440px] bg-[var(--cream-deep)]">
              <img
                src="https://customer-assets.emergentagent.com/job_elegant-landing-site/artifacts/ggtwupm3_a06e74d1-7604-4345-8d34-7b0eb87c4d2a.JPG"
                alt="Neil and Clare holding up their British passports after their Spanish NLV approval"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="md:col-span-3 p-6 lg:p-8 flex flex-col">
              <div className="flex items-center gap-3 pb-4 border-b border-black/5">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[var(--terracotta)] to-[var(--gold)] flex items-center justify-center text-[var(--cream)] font-serif text-lg">
                  G
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[var(--navy)] text-sm">Get To Spain</div>
                  <div className="text-xs text-[var(--muted)] flex items-center gap-1.5">
                    <span>Neil Saunders · 19 March</span>
                    <span className="opacity-60">·</span>
                    <svg className="w-3 h-3 fill-[var(--muted)]" viewBox="0 0 16 16">
                      <path d="M8 0a8 8 0 100 16A8 8 0 008 0zM4 8h8v1H4V8zm0-3h8v1H4V5zm0 6h6v1H4v-1z" />
                    </svg>
                  </div>
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-[var(--terracotta)] font-medium hidden sm:inline">
                  Manchester BLS
                </span>
              </div>

              <blockquote className="mt-5 text-[var(--ink)] leading-relaxed text-[0.98rem] flex-1">
                "From our initial call to <span className="text-[#1B74E4] font-medium">Carley Chaplin</span> in
                mid January, all the help you gave us, to our appointment at the Manchester BLS on
                the 19th March. Today, <strong>8 weeks to the day</strong>, we received our Spanish
                NLV Visas — now our dream begins. We couldn't recommend Carley enough, very
                thorough and meticulous, and made the process easier. Thank you 🤗 ✈️ 🏝️ 🌞"
              </blockquote>

              <div className="mt-5 pt-4 border-t border-black/5 flex items-center justify-between text-xs text-[var(--muted)]">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-[#1877F2] flex items-center justify-center">
                      <svg className="w-3 h-3 text-white fill-current" viewBox="0 0 16 16"><path d="M6.956 14.5H1.5v-7h2.5v-1c0-2.485 1.515-4 4-4h2.5v3h-2c-.776 0-1 .224-1 1v1h3l-.5 3h-2.5v7h.456z M14.5 4.5l-3 1v2h-2v3h2v5l3-1v-4h2l.5-3h-2.5v-2l1.5-1z" /></svg>
                    </span>
                    <span className="w-5 h-5 rounded-full bg-[#F33E58] flex items-center justify-center text-white text-[10px]">❤</span>
                    <span>Neil &amp; Clare and others</span>
                  </span>
                </div>
                <span>via Messenger</span>
              </div>
            </div>
          </div>
        </figure>
      </div>
    </section>
  );
};

/* ---------------- FINAL CTA ---------------- */
const FinalCTA = () => (
  <section className="py-20 lg:py-24 bg-[var(--cream-deep)] editorial" data-testid="nlv-final-cta">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05]">
        Tell us where you are with <em className="text-[var(--terracotta)]">your Spain plans.</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl mx-auto">
        Answer a few quick questions and we will show you the most useful next step — whether that
        is a conversation with Carley, a planning resource or more time to prepare.
      </p>
      <a
        href="#book"
        className="pill mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
        data-testid="nlv-final-cta-btn"
      >
        Show me my next step <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
export default function NlvApproved() {
  usePageMeta(
    "NLV Approved | Personal Support for Spain's Non-Lucrative Visa",
    "Plan and prepare your Spanish Non-Lucrative Visa with personal support from Carley Chaplin, including documents, forms, evidence and next steps."
  );
  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] selection:bg-[var(--terracotta)] selection:text-[var(--cream)]" data-testid="nlv-page">
      <SiteNav />
      <Hero />
      <StopSection />
      <VideoSection />
      <Programme />
      <SolicitorBand />
      <Sequencing />
      <WhoFor />
      <About />
      <Testimonials />
      <FinalCTA />
      <BookSection />
      <SiteFooter />
      <FloatingBookCTA href="#book" label="Find my next step" />
    </main>
  );
}
